import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import {
    CSRF_HEADER_NAME,
    CSRF_PROTECTED_METHODS,
    getCsrfToken,
} from 'nera-common/lib/auth/csrf';
import { API_BASE_URL } from 'nera-common/angular';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { resyncCsrfFromSession } from 'app/core/services/auth/csrf-sync';

/**
 * Echoes the `__nera_csrf` double-submit cookie back as `X-CSRF-Token` on
 * mutating cookie-authenticated API requests.
 *
 * Mirrors the server's exemptions exactly, so no request carries a credential
 * the server will ignore: safe methods and `Authorization`-bearing requests
 * are never validated (MUST run after `neraAuthInterceptor`, which attaches
 * that header); no `__nera_csrf` cookie means no cookie session to protect;
 * presigned MinIO PUTs (`dam-upload.service.ts`) stay header-free to avoid an
 * unanswered CORS preflight.
 *
 * Self-heal (2026-10-07): on a tenant subdomain (*.localhost) the CSRF value
 * lives in a shadow cookie synced from session responses — `me`, `refresh` and
 * any endpoint that re-issues cookies can rotate the server copy, leaving the
 * shadow stale. A CSRF_VALIDATION_FAILED 403 therefore gets ONE re-sync (GET
 * /session/me, whose body carries the live token) and a single retry with the
 * fresh header. The retry is issued via the already-executed `next`, so it
 * cannot re-enter this interceptor and loop.
 */
export const csrfInterceptor: HttpInterceptorFn = (req, next) => {
    if (!CSRF_PROTECTED_METHODS.has(req.method.toUpperCase())) {
        return next(req);
    }
    if (req.headers.has('Authorization')) {
        return next(req);
    }
    const apiBaseUrl = inject(API_BASE_URL);
    if (!req.url.startsWith('/api/') && !req.url.startsWith(apiBaseUrl)) {
        return next(req);
    }
    const token = getCsrfToken();
    const authed = token
        ? req.clone({ setHeaders: { [CSRF_HEADER_NAME]: token } })
        : req;
    return next(authed).pipe(
        catchError((err: HttpErrorResponse) => {
            // ApiErrorResponse shape: { status, message, error: { code, ... } }
            const code = (
                err.error as { error?: { code?: string } } | null
            )?.error?.code;
            if (err.status !== 403 || code !== 'CSRF_VALIDATION_FAILED') {
                return throwError(() => err);
            }
            return from(resyncCsrfFromSession()).pipe(
                switchMap((synced) => {
                    const fresh = getCsrfToken();
                    if (!synced || !fresh || fresh === token) {
                        return throwError(() => err);
                    }
                    return next(
                        req.clone({ setHeaders: { [CSRF_HEADER_NAME]: fresh } })
                    );
                })
            );
        })
    );
};
